import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowLeft, UserPlus } from 'lucide-react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import AdminSidebar from '../../../components/sidebar/AdminSidebar';
import OrganizationMemberFormModal from '../../../components/admin/OrganizationMemberFormModal';
import OrganizationUsageDashboard from '../../../components/admin/usage/OrganizationUsageDashboard';
import CreditManagementSection from '../../../components/admin/CreditManagementSection';
import LoginHistoryList from '../../../components/admin/detail/LoginHistoryList';
import GeneratedContentPanel from '../../../components/admin/detail/GeneratedContentPanel';
import CompetitorMonitoringControl from './components/CompetitorMonitoringControl';
import adminUserDetailService, { AdminContentType } from '../../../services/api/adminUserDetailService';
import { RootState } from '../../../redux/store';
import {
  fetchOrganizationDetails,
  updateOrganization,
  addOrUpdateOrganizationMember,
  removeOrganizationMember,
  updateOrganizationAgentSettings,
  setSelectedOrganization,
  toggleOrganizationActive,
  sendMemberLoginEmail
} from '../../../redux/admin/adminSlice';
import { OrganizationMember, OrganizationAgent, PlanType, linkOrganizationStripe, extendOrganizationTrial } from '../../../services/api/adminOrganizationService';
import { cn } from '../../../lib/utils';
import { formatLimit } from '../../../utils/limitDisplay';
import {
  ResponsiveAppShell,
  TaskyBadge,
  TaskyButton,
  TaskyErrorState,
  TaskyInput,
  TaskySelect,
  TaskySpinner,
  TaskyTextarea,
  useConfirm,
} from '../../../components/tasky';
import { useToast } from '../../../contexts/ToastContext';

// タブの定義
type OrganizationTab = 'info' | 'members' | 'agents' | 'credits' | 'usage' | 'login-histories' | 'contents';

// プラン定義
const PLAN_OPTIONS: { value: PlanType; label: string; credit: string; concurrent: number; products: string; monitoring: boolean }[] = [
  { value: 'demo', label: 'デモ', credit: '35,000', concurrent: 10, products: '無制限', monitoring: true },
  { value: 'personal', label: 'パーソナル', credit: '10,000', concurrent: 10, products: '3', monitoring: false },
  { value: 'team', label: 'チーム', credit: '35,000', concurrent: 10, products: '3', monitoring: true },
  { value: 'agency', label: 'エージェンシー', credit: '60,000', concurrent: 12, products: '無制限', monitoring: true },
  { value: 'enterprise', label: 'エンタープライズ', credit: 'カスタム', concurrent: 20, products: '無制限', monitoring: true },
];

// タブ表示定義
const TAB_ITEMS: { key: OrganizationTab; label: string }[] = [
  { key: 'info', label: '組織情報' },
  { key: 'members', label: 'メンバー一覧' },
  { key: 'agents', label: 'AIエージェント設定' },
  { key: 'credits', label: 'クレジット' },
  { key: 'usage', label: '使用状況' },
  { key: 'login-histories', label: 'ログイン履歴' },
  { key: 'contents', label: '生成物' },
];

// 情報グリッドの項目ラベル
const InfoLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs text-gray-400 mb-1">{children}</p>
);

const OrganizationDetailPage: React.FC = () => {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { id } = useParams<{ id: string }>();
  const organizationId = parseInt(id || '0');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Redux状態
  const {
    organizationDetails,
    selectedOrganization,
    isLoading,
    error,
  } = useSelector((state: RootState) => state.admin);
  const { user } = useSelector((state: RootState) => state.auth);

  // ローカルステート
  const [activeTab, setActiveTab] = useState<OrganizationTab>('info');
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<OrganizationMember | null>(null);
  const [isEditingOrganization, setIsEditingOrganization] = useState(false);
  const [editedOrgData, setEditedOrgData] = useState<{
    name: string;
    description: string;
    plan: PlanType | null;
    member_limit: number;
    monthly_credit_limit: number;
    daily_free_credits: number;
  } | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  // Stripe 連携編集
  const [isEditingStripe, setIsEditingStripe] = useState(false);
  const [stripeForm, setStripeForm] = useState<{
    stripe_customer_id: string;
    stripe_subscription_id: string;
    apply_plan_provisioning: boolean;
  }>({ stripe_customer_id: '', stripe_subscription_id: '', apply_plan_provisioning: false });
  const [isSavingStripe, setIsSavingStripe] = useState(false);
  // トライアル延長編集
  const [isEditingTrial, setIsEditingTrial] = useState(false);
  const [trialEndsAtInput, setTrialEndsAtInput] = useState('');
  const [isSavingTrial, setIsSavingTrial] = useState(false);
  const totalUsers = organizationDetails
    ? organizationDetails.organization.total_users ?? organizationDetails.members.length
    : 0;
  const leaderCount = organizationDetails
    ? organizationDetails.organization.leader_count ?? organizationDetails.members.filter(member => member.role === 'leader').length
    : 0;
  const memberCount = organizationDetails
    ? organizationDetails.organization.member_count ?? organizationDetails.members.filter(member => member.role === 'member').length
    : 0;

  // 組織詳細データ取得
  useEffect(() => {
    if (organizationId) {
      console.log('OrganizationDetailPage: 組織詳細取得開始', organizationId);
      setHasFetched(false);
      dispatch(fetchOrganizationDetails(organizationId)).finally(() => {
        setHasFetched(true);
      });
    }
  }, [dispatch, organizationId]);

  // デバッグ情報
  useEffect(() => {
    console.log('OrganizationDetailPage: 状態更新', {
      isLoading,
      hasFetched,
      hasDetails: !!organizationDetails,
      error,
    });
  }, [organizationDetails, isLoading, error, hasFetched]);

  // 組織情報がない場合、一覧ページにリダイレクト
  useEffect(() => {
    if (hasFetched && !isLoading && !organizationDetails && !error) {
      console.log('OrganizationDetailPage: データなし、リダイレクト');
      navigate('/admin');
    }
  }, [navigate, organizationDetails, isLoading, error, hasFetched]);

  // ログイン履歴・生成物タブ用のページ取得コールバック（10件/頁）
  const fetchOrgLoginHistoryPage = useCallback(
    (page: number) => adminUserDetailService.getOrganizationLoginHistories(organizationId, { page }),
    [organizationId]
  );

  const fetchOrgContentPage = useCallback(
    (type: AdminContentType, page: number) => adminUserDetailService.getOrganizationContents(organizationId, type, { page }),
    [organizationId]
  );

  // 戻るボタン処理
  const handleBack = () => {
    dispatch(setSelectedOrganization(null));
    navigate('/admin');
  };

  // 組織の有効/無効を切り替える処理
  const handleToggleOrganizationActive = async () => {
    if (!organizationDetails) return;

    const isCurrentlyActive = organizationDetails.organization.is_active;
    const actionText = isCurrentlyActive ? '無効化' : '有効化';

    if (await confirm({ title: `この組織を${actionText}してもよろしいですか？`, description: isCurrentlyActive ? '無効化すると、所属ユーザーはログインできなくなります。' : undefined, tone: isCurrentlyActive ? 'destructive' : 'warning', confirmText: actionText })) {
      try {
        await dispatch(toggleOrganizationActive({
          id: organizationId,
          isActive: !isCurrentlyActive
        })).unwrap();
      } catch (error) {
        console.error(`Failed to ${actionText} organization:`, error);
      }
    }
  };

  // メンバー追加モーダルを開く
  const handleOpenMemberModal = () => {
    setIsMemberModalOpen(true);
  };

  // メンバー追加モーダルを閉じる
  const handleCloseMemberModal = () => {
    setIsMemberModalOpen(false);
  };

  // メンバー追加処理
  const handleAddMember = async (memberData: { name: string; email: string; role: string; password?: string }) => {
    try {
      console.log('メンバー追加リクエスト:', memberData);
      const result = await dispatch(addOrUpdateOrganizationMember({
        organizationId,
        data: {
          email: memberData.email,
          role: memberData.role as 'leader' | 'member',
          name: memberData.name,
          password: memberData.password
        }
      })).unwrap();

      // 成功後、モーダルを閉じてメンバー一覧を更新
      setIsMemberModalOpen(false);
      dispatch(fetchOrganizationDetails(organizationId));

      // 自動生成されたパスワードがある場合は表示
      if (result?.data?.generated_password) {
        await confirm({
          title: 'メンバーを追加しました。',
          description: `自動生成されたパスワード: ${result.data.generated_password}\n\nこのパスワードは一度しか表示されません。メンバーに伝えてください。`,
          tone: 'info',
          confirmText: 'OK',
          hideCancel: true,
        });
      } else {
        showToast('メンバーを追加しました。', 'success');
      }
    } catch (error) {
      console.error('Failed to add member:', error);
      showToast('メンバーの追加に失敗しました。既に組織に所属している可能性があります。', 'error');
    }
  };

  // メンバー編集処理
  const handleEditMember = (member: any) => {
    setEditingMember(member);
    setIsEditModalOpen(true);
  };

  // メンバー更新処理
  const handleUpdateMember = async (memberData: { name: string; email: string; role: string }) => {
    if (!editingMember) return;

    try {
      console.log('メンバー更新リクエスト:', memberData);
      await dispatch(addOrUpdateOrganizationMember({
        organizationId,
        data: {
          user_id: editingMember.id,
          email: memberData.email,
          role: memberData.role as 'leader' | 'member',
          name: memberData.name
        }
      })).unwrap();

      setIsEditModalOpen(false);
      setEditingMember(null);
      dispatch(fetchOrganizationDetails(organizationId));
      showToast('メンバー情報を更新しました。', 'success');
    } catch (error) {
      console.error('Failed to update member:', error);
      showToast('メンバーの更新に失敗しました。', 'error');
    }
  };

  // 組織情報編集処理
  const handleEditOrganization = () => {
    setEditedOrgData({
      name: organizationDetails?.organization.name || '',
      description: organizationDetails?.organization.description || '',
      plan: organizationDetails?.organization.plan || null,
      member_limit: organizationDetails?.organization.member_limit || 10,
      monthly_credit_limit: organizationDetails?.organization.monthly_credit_limit || 0,
      daily_free_credits: organizationDetails?.organization.daily_free_credits || 0
    });
    setIsEditingOrganization(true);
  };

  // 組織情報保存処理
  const handleSaveOrganization = async () => {
    if (!editedOrgData) return;

    // プランが変更された場合は確認ダイアログ
    const currentPlan = organizationDetails?.organization.plan || null;
    if (editedOrgData.plan !== currentPlan && editedOrgData.plan) {
      const planLabel = PLAN_OPTIONS.find(p => p.value === editedOrgData.plan)?.label || editedOrgData.plan;
      if (!(await confirm({ title: `プランを「${planLabel}」に変更します。`, description: 'クレジット上限・同時実行数・商品登録上限・競合モニタリングの設定がプランのデフォルト値に変更されます。\n\nよろしいですか？', confirmText: '変更する' }))) {
        return;
      }
    }

    try {
      await dispatch(updateOrganization({
        id: organizationId,
        data: editedOrgData
      })).unwrap();

      setIsEditingOrganization(false);
      setEditedOrgData(null);
      dispatch(fetchOrganizationDetails(organizationId));
      showToast('組織情報を更新しました。', 'success');
    } catch (error) {
      console.error('Failed to update organization:', error);
      showToast('組織情報の更新に失敗しました。', 'error');
    }
  };

  // Stripe 連携編集開始
  const handleEditStripe = () => {
    setStripeForm({
      stripe_customer_id: (organizationDetails?.organization as any)?.stripe_customer_id || '',
      stripe_subscription_id: (organizationDetails?.organization as any)?.stripe_subscription_id || '',
      apply_plan_provisioning: false,
    });
    setIsEditingStripe(true);
  };

  // Stripe 連携保存処理（紐付け・解除）
  const handleSaveStripeLink = async () => {
    const customerId = stripeForm.stripe_customer_id.trim();
    const subscriptionId = stripeForm.stripe_subscription_id.trim();

    // 顧客IDを空にすると紐付け解除
    if (!customerId) {
      if (!(await confirm({ title: 'Stripe 連携を解除します。よろしいですか？', tone: 'destructive', confirmText: '解除する' }))) return;
    } else if (stripeForm.apply_plan_provisioning) {
      if (!(await confirm({ title: 'サブスクリプションのプランに合わせてクレジット上限・プランを同期します。よろしいですか？', confirmText: '同期する' }))) return;
    }

    setIsSavingStripe(true);
    try {
      await linkOrganizationStripe(organizationId, {
        stripe_customer_id: customerId || null,
        stripe_subscription_id: subscriptionId || null,
        apply_plan_provisioning: stripeForm.apply_plan_provisioning,
      });
      setIsEditingStripe(false);
      dispatch(fetchOrganizationDetails(organizationId));
      showToast('Stripe 連携を更新しました。', 'success');
    } catch (error: any) {
      console.error('Failed to update stripe link:', error);
      showToast(error?.message || 'Stripe 連携の更新に失敗しました。', 'error');
    } finally {
      setIsSavingStripe(false);
    }
  };

  // トライアル延長の実行（クイック延長・終了日指定の共通処理）
  const saveTrialExtension = async (payload: { days?: number; trial_ends_at?: string }) => {
    setIsSavingTrial(true);
    try {
      const result = await extendOrganizationTrial(organizationId, payload);
      setIsEditingTrial(false);
      dispatch(fetchOrganizationDetails(organizationId));
      const endsAt = result.trial_ends_at
        ? new Date(result.trial_ends_at).toLocaleString('ja-JP')
        : '';
      showToast(`トライアル期間を延長しました。\n新しい終了日時: ${endsAt}`, 'success');
    } catch (error: any) {
      console.error('Failed to extend trial:', error);
      showToast(error?.message || 'トライアル期間の延長に失敗しました。', 'error');
    } finally {
      setIsSavingTrial(false);
    }
  };

  // トライアル延長編集開始（現在の終了日を初期値に。未設定なら本日）
  const handleEditTrial = () => {
    const currentEndsAt = (organizationDetails?.organization as any)?.trial_ends_at as string | null | undefined;
    const base = currentEndsAt ? new Date(currentEndsAt) : new Date();
    // toISOString() はUTCに変換され日本時間の朝に前日へずれるため、ローカル日付から組み立てる
    const yyyy = base.getFullYear();
    const mm = String(base.getMonth() + 1).padStart(2, '0');
    const dd = String(base.getDate()).padStart(2, '0');
    setTrialEndsAtInput(`${yyyy}-${mm}-${dd}`);
    setIsEditingTrial(true);
  };

  // クイック延長（日数指定）
  const handleExtendTrialByDays = async (days: number) => {
    const isInTrial = Boolean((organizationDetails?.organization as any)?.is_in_trial);
    const origin = isInTrial ? '現在の終了日' : '本日';
    if (!(await confirm({ title: `トライアル期間を${origin}から${days}日延長します。よろしいですか？`, confirmText: '延長する' }))) return;
    await saveTrialExtension({ days });
  };

  // 終了日を直接指定して保存
  const handleSaveTrialEndDate = async () => {
    if (!trialEndsAtInput) {
      showToast('終了日を入力してください。', 'error');
      return;
    }
    if (!(await confirm({ title: `トライアル終了日を ${trialEndsAtInput} に変更します。よろしいですか？`, confirmText: '変更する' }))) return;
    await saveTrialExtension({ trial_ends_at: trialEndsAtInput });
  };

  // メンバー削除処理
  const handleRemoveMember = async (userId: number) => {
    if (await confirm({ title: 'このメンバーを組織から削除してもよろしいですか？', tone: 'destructive', confirmText: '削除する' })) {
      try {
        await dispatch(removeOrganizationMember({ organizationId, userId })).unwrap();
      } catch (error) {
        console.error('Failed to remove member:', error);
      }
    }
  };

  // ログイン情報メール送信処理
  const handleSendLoginEmail = async (userId: number, memberName: string) => {
    if (await confirm({ title: `${memberName}にログイン情報メールを送信しますか？`, description: '送信後、現在のパスワードは無効になり、メール内のリンクから新しいパスワードを設定する必要があります。', confirmText: '送信する' })) {
      try {
        await dispatch(sendMemberLoginEmail({ organizationId, userId })).unwrap();
        showToast('ログイン情報メールを送信しました。', 'success');
      } catch (error) {
        console.error('Failed to send login email:', error);
        showToast('ログイン情報メールの送信に失敗しました。', 'error');
      }
    }
  };

  // エージェント設定更新処理
  const handleToggleAgentEnabled = async (agentId: number, isEnabled: boolean) => {
    if (!organizationDetails) return;

    try {
      // 現在の設定を取得
      const agentSettings = organizationDetails.agents.map(agent => ({
        agent_id: agent.id,
        is_enabled: agent.id === agentId ? !isEnabled : agent.is_enabled_for_organization
      }));

      // 設定を更新
      await dispatch(updateOrganizationAgentSettings({
        organizationId,
        settings: { agent_settings: agentSettings }
      })).unwrap();
    } catch (error) {
      console.error('Failed to update agent settings:', error);
    }
  };

  if (isLoading || !hasFetched) {
    return (
      <div className="flex items-center gap-3 justify-center py-12">
        <TaskySpinner size="md" />
        <span className="text-sm text-gray-600">データを読み込み中...</span>
      </div>
    );
  }

  if (!organizationDetails) {
    return (
      <div className="p-6">
        <TaskyErrorState
          title="組織情報を取得できませんでした"
          description="ページを再読み込みしてもう一度お試しください。"
        />
      </div>
    );
  }

  const organization = organizationDetails.organization;
  const adminName = user?.name || '管理者';
  const desktopSidebar = (
    <AdminSidebar
      activePage="organizations"
      onPageChange={(page) => navigate(`/admin${page === 'organizations' ? '' : `/${page}`}`)}
      adminName={adminName}
    />
  );
  const mobileSidebar = (
    <div className="flex min-h-full flex-col bg-dark py-4 text-white">
      <div className="px-5 pb-4">
        <p className="text-lg font-bold">管理パネル</p>
        <p className="mt-1 truncate text-xs text-white/60">{organization.name}</p>
      </div>
      <button
        type="button"
        onClick={handleBack}
        className="mx-3 mb-3 rounded-tasky-sm border border-white/15 px-3 py-2 text-left text-sm text-white/[0.85]"
      >
        組織一覧へ戻る
      </button>
      <nav className="min-h-0 flex-1 overflow-y-auto">
        {TAB_ITEMS.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex w-full items-center px-5 py-3 text-left text-sm transition-colors ${
              activeTab === tab.key
                ? 'bg-white/[0.12] font-semibold text-white'
                : 'text-white/80 hover:bg-white/[0.08] hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>
      <div className="border-t border-white/10 px-5 pt-4">
        <p className="text-sm font-semibold">{adminName}</p>
        <p className="mt-1 text-xs text-white/60">管理者</p>
      </div>
    </div>
  );

  return (
    <ResponsiveAppShell
      sidebar={desktopSidebar}
      mobileSidebar={mobileSidebar}
      topbarTitle="組織詳細"
      className="bg-warm-bg"
    >
      <div className="min-h-[calc(100dvh-56px)] overflow-y-auto overflow-x-hidden bg-warm-bg lg:ml-[250px] lg:h-screen lg:min-h-0">
        <div className="mx-auto max-w-5xl px-3 py-4 sm:px-6 sm:py-6">
          {/* ヘッダー */}
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <TaskyButton variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft />
              戻る
            </TaskyButton>
            <h1 className="min-w-0 text-xl font-semibold text-dark sm:truncate">{organization.name}</h1>
            <TaskyBadge variant={organization.is_active ? 'success' : 'secondary'}>
              {organization.is_active ? '有効' : '無効'}
            </TaskyBadge>
            <TaskyButton
              variant="ghost"
              size="sm"
              className="sm:ml-auto"
              onClick={() => navigate(`/admin/behavior?tab=sessions&organization_id=${organization.id}`)}
            >
              行動ログ
            </TaskyButton>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-destructive text-sm rounded-tasky-sm px-4 py-3 mb-4">
              {error}
            </div>
          )}

          {/* タブナビゲーション */}
          <div className="border-b border-warm-border mb-6">
            <div className="flex overflow-x-auto">
              {TAB_ITEMS.map(tab => (
                <div
                  key={tab.key}
                  className={cn(
                    'whitespace-nowrap pb-3 px-4 text-sm font-medium border-b-2 cursor-pointer transition-colors',
                    activeTab === tab.key
                      ? 'text-primary border-primary'
                      : 'text-gray-500 border-transparent hover:text-gray-700'
                  )}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </div>
              ))}
            </div>
          </div>

          <div>
            {/* 組織情報タブ */}
            {activeTab === 'info' && (
              <div className="rounded-tasky-md bg-white p-4 sm:p-6">
                <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="text-sm font-semibold text-dark">基本情報</h2>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    {!isEditingOrganization ? (
                      <TaskyButton variant="outline" size="sm" onClick={handleEditOrganization}>編集</TaskyButton>
                    ) : (
                      <>
                        <TaskyButton variant="outline" size="sm" onClick={() => { setIsEditingOrganization(false); setEditedOrgData(null); }}>キャンセル</TaskyButton>
                        <TaskyButton size="sm" onClick={handleSaveOrganization}>保存</TaskyButton>
                      </>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
                  <div>
                    <InfoLabel>組織名</InfoLabel>
                    {!isEditingOrganization ? (
                      <p className="text-sm text-dark">{organization.name}</p>
                    ) : (
                      <TaskyInput
                        className="h-9"
                        type="text"
                        value={editedOrgData?.name || ''}
                        onChange={(e) => setEditedOrgData({...editedOrgData, name: e.target.value})}
                      />
                    )}
                  </div>

                  <div>
                    <InfoLabel>説明</InfoLabel>
                    {!isEditingOrganization ? (
                      <p className="text-sm text-dark">{organization.description || '(なし)'}</p>
                    ) : (
                      <TaskyTextarea
                        rows={3}
                        value={editedOrgData?.description || ''}
                        onChange={(e) => setEditedOrgData({...editedOrgData, description: e.target.value})}
                      />
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <InfoLabel>契約プラン</InfoLabel>
                    {!isEditingOrganization ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm text-dark">
                          {organization.plan
                            ? PLAN_OPTIONS.find(p => p.value === organization.plan)?.label || organization.plan
                            : '(未設定)'}
                        </p>
                        {organization.plan_locked_at && (
                          <span
                            className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800"
                            title="Stripe の請求プランと意図的に食い違う手動設定です。Webhook からのプラン上書きを止めています。"
                          >
                            手動プラン固定中（請求額据え置き）
                          </span>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="max-w-xl">
                          <TaskySelect
                            className="h-9 text-sm"
                            value={editedOrgData?.plan || ''}
                            onChange={(e) => {
                              const newPlan = (e.target.value || null) as PlanType | null;
                              setEditedOrgData({...editedOrgData!, plan: newPlan});
                            }}
                          >
                            <option value="">未設定</option>
                            {PLAN_OPTIONS.map(plan => (
                              <option key={plan.value} value={plan.value}>
                                {plan.label} (Credit: {plan.credit} / 同時実行: {plan.concurrent} / 商品: {plan.products})
                              </option>
                            ))}
                          </TaskySelect>
                        </div>
                        {editedOrgData?.plan === 'enterprise' && (
                          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                            <span className="text-xs text-gray-500">クレジット上限(カスタム):</span>
                            <TaskyInput
                              type="number"
                              className="h-9 w-full sm:w-40"
                              value={editedOrgData?.monthly_credit_limit || 0}
                              onChange={(e) => setEditedOrgData({...editedOrgData!, monthly_credit_limit: parseInt(e.target.value) || 0})}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div>
                    <InfoLabel>商品登録上限</InfoLabel>
                    <p className="text-sm text-dark">
                      {formatLimit(organization.max_product_registrations)}
                    </p>
                  </div>

                  <div>
                    <InfoLabel>競合モニタリング</InfoLabel>
                    {/* enable_competitor_monitoring のバッジはスケジューラが参照しておらず実態と無関係だったため、
                        実際に巡回を止める打刻（competitor_monitoring_disabled_at）の操作に置き換えた。 */}
                    <CompetitorMonitoringControl
                      organizationId={organizationId}
                      disabledAt={organization.competitor_monitoring_disabled_at ?? null}
                      activeSiteCount={organization.active_monitored_site_count ?? 0}
                      onUpdated={() => { dispatch(fetchOrganizationDetails(organizationId)); }}
                    />
                  </div>

                  <div>
                    <InfoLabel>ユーザー数</InfoLabel>
                    {!isEditingOrganization ? (
                      <p className="text-sm text-dark">
                        {totalUsers}
                        <span className="ml-2 text-xs text-gray-500">
                          リーダー {leaderCount} / メンバー {memberCount}
                        </span>
                      </p>
                    ) : (
                      <div className="flex flex-col gap-2 text-sm text-dark sm:flex-row sm:items-center">
                        <span>{totalUsers}（メンバー上限: </span>
                        <TaskyInput
                          className="h-8 w-full sm:w-20"
                          type="number"
                          value={editedOrgData?.member_limit || 0}
                          onChange={(e) => setEditedOrgData({...editedOrgData, member_limit: parseInt(e.target.value) || 0})}
                        />
                        <span>）</span>
                        <span className="text-xs text-gray-500">-1 で無制限</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <InfoLabel>月間クレジット上限</InfoLabel>
                    {!isEditingOrganization ? (
                      <p className="text-sm text-dark">
                        {organization.monthly_credit_limit === null || organization.monthly_credit_limit === undefined
                          ? '無制限'
                          : organization.monthly_credit_limit.toLocaleString()}
                      </p>
                    ) : (
                      <TaskyInput
                        type="number"
                        value={editedOrgData?.monthly_credit_limit || 0}
                        onChange={(e) => setEditedOrgData({...editedOrgData!, monthly_credit_limit: parseInt(e.target.value) || 0})}
                        className="h-9 w-full sm:w-40"
                      />
                    )}
                  </div>

                  <div>
                    <InfoLabel>月間トークン上限</InfoLabel>
                    <p className="text-sm text-dark">
                      {organization.monthly_token_limit === null || organization.monthly_token_limit === undefined
                        ? '無制限'
                        : Number(organization.monthly_token_limit).toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <InfoLabel>今月の使用クレジット</InfoLabel>
                    <p className="text-sm text-dark">{Math.round(organization.monthly_credits_used || 0).toLocaleString()}</p>
                  </div>

                  <div>
                    <InfoLabel>残りクレジット</InfoLabel>
                    <p className="text-sm text-dark">{Math.round(organization.remaining_credits || 0).toLocaleString()}</p>
                  </div>

                  <div>
                    <InfoLabel>今月の使用トークン</InfoLabel>
                    <p className="text-sm text-dark">{Number(organization.monthly_tokens_used || 0).toLocaleString()}</p>
                  </div>

                  <div>
                    <InfoLabel>作成日</InfoLabel>
                    <p className="text-sm text-dark">{new Date(organization.created_at).toLocaleDateString('ja-JP')}</p>
                  </div>

                  <div>
                    <InfoLabel>招待コード</InfoLabel>
                    <p className="text-sm text-dark">
                      {(organization as any).invitation_code || <em className="text-gray-400 not-italic">未設定</em>}
                    </p>
                  </div>

                  <div>
                    <InfoLabel>招待付与クレジット</InfoLabel>
                    <p className="text-sm text-dark">
                      {Number((organization as any).invitation_credit_amount || 0).toLocaleString()}
                    </p>
                  </div>

                  <div className="sm:col-span-2">
                    <div className="mb-2 flex items-center justify-between">
                      <InfoLabel>トライアル状況</InfoLabel>
                      {!isEditingTrial && (
                        <TaskyButton size="sm" variant="outline" onClick={handleEditTrial}>
                          延長
                        </TaskyButton>
                      )}
                    </div>
                    <p className="text-sm">
                      {(organization as any).is_in_trial ? (
                        <span className="text-success font-semibold">
                          トライアル中
                          {(organization as any).trial_ends_at && (
                            <> （{new Date((organization as any).trial_ends_at).toLocaleDateString('ja-JP')} まで）</>
                          )}
                        </span>
                      ) : (organization as any).trial_has_expired ? (
                        <span className="text-destructive">
                          トライアル終了
                          {(organization as any).trial_ends_at && (
                            <> （{new Date((organization as any).trial_ends_at).toLocaleDateString('ja-JP')} 終了）</>
                          )}
                        </span>
                      ) : (
                        <em className="text-gray-400 not-italic">対象外</em>
                      )}
                    </p>

                    {isEditingTrial && (
                      <div className="mt-3 space-y-3 rounded-tasky-md bg-warm-surface p-3">
                        <div>
                          <InfoLabel>クイック延長</InfoLabel>
                          <div className="flex flex-wrap gap-2">
                            {[7, 14, 30].map(days => (
                              <TaskyButton
                                key={days}
                                size="sm"
                                variant="outline"
                                onClick={() => handleExtendTrialByDays(days)}
                                disabled={isSavingTrial}
                              >
                                +{days}日
                              </TaskyButton>
                            ))}
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            {(organization as any).is_in_trial
                              ? '現在の終了日に加算します。'
                              : 'トライアルが終了・未開始のため、本日から起算します。'}
                          </p>
                        </div>

                        <div>
                          <InfoLabel>終了日を指定</InfoLabel>
                          <div className="flex flex-wrap items-center gap-2">
                            <TaskyInput
                              type="date"
                              className="h-9 w-44"
                              value={trialEndsAtInput}
                              onChange={(e) => setTrialEndsAtInput(e.target.value)}
                            />
                            <TaskyButton size="sm" onClick={handleSaveTrialEndDate} disabled={isSavingTrial}>
                              {isSavingTrial ? '保存中...' : '保存'}
                            </TaskyButton>
                            <TaskyButton
                              size="sm"
                              variant="outline"
                              onClick={() => setIsEditingTrial(false)}
                              disabled={isSavingTrial}
                            >
                              キャンセル
                            </TaskyButton>
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            指定した日の23:59まで利用できます。延長されるのは期間のみで、クレジットは「クレジット」タブから別途付与してください。
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <InfoLabel>Stripe顧客</InfoLabel>
                    <p className="text-sm">
                      {(organization as any).has_stripe_customer ? (
                        <>
                          <span className="text-success font-semibold">紐付け済</span>
                          <code className="mt-1 block break-all text-[11px] text-gray-500 sm:ml-2 sm:mt-0 sm:inline">
                            {(organization as any).stripe_customer_id}
                          </code>
                        </>
                      ) : (
                        <em className="text-gray-400 not-italic">未登録</em>
                      )}
                    </p>
                  </div>

                  <div>
                    <InfoLabel>Stripeサブスク</InfoLabel>
                    <p className="text-sm">
                      {(organization as any).has_stripe_subscription ? (
                        <>
                          <span className="text-success font-semibold">有効</span>
                          <code className="mt-1 block break-all text-[11px] text-gray-500 sm:ml-2 sm:mt-0 sm:inline">
                            {(organization as any).stripe_subscription_id}
                          </code>
                        </>
                      ) : (
                        <em className="text-gray-400 not-italic">未登録</em>
                      )}
                    </p>
                  </div>

                  {/* Stripe 連携（手動紐付け・解除） */}
                  <div className="border-t border-warm-border pt-3 sm:col-span-2">
                    <div className="mb-2 flex items-center justify-between">
                      <InfoLabel>Stripe 連携（手動紐付け）</InfoLabel>
                      {!isEditingStripe && (
                        <TaskyButton size="sm" variant="outline" onClick={handleEditStripe}>
                          編集
                        </TaskyButton>
                      )}
                    </div>

                    {!isEditingStripe ? (
                      <p className="text-xs text-gray-500">
                        後からカードを登録した企業や振込企業を Stripe の顧客ID（cus_）と紐付けると、以後の Webhook が自動反映されます。
                      </p>
                    ) : (
                      <div className="space-y-3 rounded-tasky-md bg-warm-surface p-3">
                        <div>
                          <InfoLabel>Stripe 顧客ID（cus_ / 空欄で解除）</InfoLabel>
                          <TaskyInput
                            className="h-9 w-full"
                            placeholder="cus_xxxxxxxx"
                            value={stripeForm.stripe_customer_id}
                            onChange={(e) => setStripeForm({ ...stripeForm, stripe_customer_id: e.target.value })}
                          />
                        </div>
                        <div>
                          <InfoLabel>Stripe サブスクリプションID（sub_ / 任意）</InfoLabel>
                          <TaskyInput
                            className="h-9 w-full"
                            placeholder="sub_xxxxxxxx"
                            value={stripeForm.stripe_subscription_id}
                            onChange={(e) => setStripeForm({ ...stripeForm, stripe_subscription_id: e.target.value })}
                          />
                        </div>
                        <label className="flex items-center gap-2 text-xs text-dark">
                          <input
                            type="checkbox"
                            checked={stripeForm.apply_plan_provisioning}
                            onChange={(e) => setStripeForm({ ...stripeForm, apply_plan_provisioning: e.target.checked })}
                          />
                          サブスクのプランに合わせてクレジット上限・プランを同期する
                        </label>
                        <div className="flex gap-2">
                          <TaskyButton size="sm" onClick={handleSaveStripeLink} disabled={isSavingStripe}>
                            {isSavingStripe ? '保存中...' : '保存'}
                          </TaskyButton>
                          <TaskyButton
                            size="sm"
                            variant="outline"
                            onClick={() => setIsEditingStripe(false)}
                            disabled={isSavingStripe}
                          >
                            キャンセル
                          </TaskyButton>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <InfoLabel>状態</InfoLabel>
                    <TaskyBadge
                      variant={organization.is_active ? 'success' : organization.cancelled_at ? 'destructive' : 'secondary'}
                    >
                      {organization.is_active ? '有効' : organization.cancelled_at ? '解約済み' : '無効'}
                    </TaskyBadge>
                  </div>

                  {organization.cancelled_at && (
                    <div>
                      <InfoLabel>解約日</InfoLabel>
                      <p className="text-sm text-destructive">
                        {new Date(organization.cancelled_at).toLocaleDateString('ja-JP')}
                      </p>
                    </div>
                  )}

                  <div className="border-t border-warm-border pt-2 sm:col-span-2">
                    <TaskyButton
                      size="sm"
                      variant={organization.is_active ? 'outline' : 'default'}
                      onClick={handleToggleOrganizationActive}
                    >
                      {organization.is_active ? '無効にする' : organization.cancelled_at ? '復活させる' : '有効にする'}
                    </TaskyButton>
                  </div>
                </div>
              </div>
            )}

            {/* メンバー一覧タブ */}
            {activeTab === 'members' && (
              <div>
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-500">全{organizationDetails.members.length}名のメンバー</p>
                  <TaskyButton size="sm" onClick={handleOpenMemberModal}>
                    <UserPlus />
                    メンバー追加
                  </TaskyButton>
                </div>

                {organizationDetails.members.length > 0 ? (
                  <div className="overflow-x-auto rounded-tasky-md bg-white">
                    <table className="min-w-[820px] text-sm">
                      <thead>
                        <tr className="bg-warm-surface">
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">名前</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">メールアドレス</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ロール</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AI利用スコア</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">登録日</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">アクション</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-warm-border">
                        {organizationDetails.members.map(member => (
                          <tr key={member.id} className="hover:bg-warm-bg transition-colors">
                            <td className="px-4 py-3 text-gray-500">{member.id}</td>
                            <td className="px-4 py-3 font-medium text-dark">{member.name}</td>
                            <td className="px-4 py-3 text-gray-500">{member.email}</td>
                            <td className="px-4 py-3">
                              <span
                                className={cn(
                                  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                                  member.role === 'leader'
                                    ? 'bg-primary/10 text-primary'
                                    : 'bg-warm-surface text-gray-600'
                                )}
                              >
                                {member.role === 'leader' ? 'リーダー' : 'メンバー'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-700">{member.ai_usage_score}</td>
                            <td className="px-4 py-3 text-gray-500 text-xs">{new Date(member.created_at).toLocaleDateString('ja-JP')}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <button
                                  className="text-sm text-primary hover:text-primary-hover font-medium transition-colors px-1.5 py-0.5"
                                  onClick={() => handleEditMember(member)}
                                >
                                  編集
                                </button>
                                <button
                                  className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors px-1.5 py-0.5"
                                  onClick={() => handleSendLoginEmail(member.id, member.name)}
                                >
                                  メール送信
                                </button>
                                <button
                                  className="text-sm text-destructive hover:opacity-80 font-medium transition-colors px-1.5 py-0.5"
                                  onClick={() => handleRemoveMember(member.id)}
                                >
                                  削除
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="bg-white rounded-tasky-md p-6 text-sm text-gray-500">メンバーがいません。</p>
                )}
              </div>
            )}

            {/* AIエージェント設定タブ */}
            {activeTab === 'agents' && (
              <div>
                <div className="mb-4">
                  <h2 className="text-sm font-semibold text-dark">AIエージェント設定</h2>
                  <p className="text-xs text-gray-400 mt-1">組織で利用可能なAIエージェントを設定します</p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {organizationDetails.agents.map(agent => (
                    <div
                      key={agent.id}
                      className={cn(
                        'bg-white rounded-tasky-md p-5 flex flex-col',
                        agent.is_enabled_for_organization && 'ring-1 ring-primary/30'
                      )}
                    >
                      <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <h3 className="text-sm font-semibold text-dark">{agent.title}</h3>
                        <TaskyBadge variant={agent.is_enabled_for_organization ? 'success' : 'secondary'}>
                          {agent.is_enabled_for_organization ? '有効' : '無効'}
                        </TaskyBadge>
                      </div>
                      <p className="text-sm text-gray-500 flex-1">
                        {agent.description}
                      </p>
                      <div className="mt-4 flex flex-col gap-3 border-t border-warm-border pt-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-gray-400">
                          モデル: <span className="text-gray-600">{agent.model_type}</span>
                        </p>
                        <TaskyButton
                          size="sm"
                          variant={agent.is_enabled_for_organization ? 'outline' : 'default'}
                          onClick={() => handleToggleAgentEnabled(agent.id, agent.is_enabled_for_organization)}
                        >
                          {agent.is_enabled_for_organization ? '無効にする' : '有効にする'}
                        </TaskyButton>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* クレジットタブ */}
            {activeTab === 'credits' && (
              <div>
                <div className="mb-4">
                  <h2 className="text-sm font-semibold text-dark">クレジット管理</h2>
                  <p className="text-xs text-gray-400 mt-1">3種類のクレジットウォレット（月間・無料・追加）を管理します</p>
                </div>
                <CreditManagementSection
                  organizationId={organizationId}
                  initialWallets={organizationDetails.credit_wallets}
                  initialTotalBalance={organizationDetails.total_credit_balance}
                />
              </div>
            )}

            {/* 使用状況タブ */}
            {activeTab === 'usage' && (
              <div>
                <div className="mb-4">
                  <h2 className="text-sm font-semibold text-dark">AI使用状況</h2>
                  <p className="text-xs text-gray-400 mt-1">組織メンバーのAI使用量を確認できます（機能別・ユーザー別）</p>
                </div>
                <div className="overflow-x-auto rounded-tasky-md bg-white p-4 sm:p-5">
                  <OrganizationUsageDashboard organizationId={organizationId} />
                </div>
              </div>
            )}

            {/* ログイン履歴タブ */}
            {activeTab === 'login-histories' && (
              <div>
                <div className="mb-4">
                  <h2 className="text-sm font-semibold text-dark">ログイン履歴</h2>
                  <p className="text-xs text-gray-400 mt-1">組織メンバーのログイン履歴を確認できます</p>
                </div>
                <LoginHistoryList fetchPage={fetchOrgLoginHistoryPage} showUserColumn />
              </div>
            )}

            {/* 生成物タブ */}
            {activeTab === 'contents' && (
              <div>
                <div className="mb-4">
                  <h2 className="text-sm font-semibold text-dark">生成物</h2>
                  <p className="text-xs text-gray-400 mt-1">組織メンバーが生成したバナー・画像・カルーセル・動画を確認できます</p>
                </div>
                <GeneratedContentPanel fetchPage={fetchOrgContentPage} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* メンバー追加モーダル */}
      <OrganizationMemberFormModal
        isOpen={isMemberModalOpen}
        onClose={handleCloseMemberModal}
        onSubmit={handleAddMember}
        organizationName={organizationDetails.organization.name}
      />

      {/* メンバー編集モーダル */}
      <OrganizationMemberFormModal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setEditingMember(null); }}
        onSubmit={handleUpdateMember}
        organizationName={organizationDetails.organization.name}
        initialData={editingMember ? {
          name: editingMember.name,
          email: editingMember.email,
          role: editingMember.role
        } : undefined}
        isEdit={true}
      />
    </ResponsiveAppShell>
  );
};

export default OrganizationDetailPage;
